import type { HighlightContext, User } from '@highlight/common/types'
import { SelectableContext } from '@shared/types/SelectableContext'
import { GlobalState } from '@highlight/ipc'
import { UpdateState } from '@shared/types/UpdateTypes.ts'
import type { PermissionsState } from '@shared/types/AudioCapturePermissions'

// key constants
export const HIGHLIGHT_CONTEXT_STATE = 'context'
export const SELECTABLE_CONTEXT_STATE = 'selectableContext'
export const ACTIVE_AUDIO_NOTE_STATE = 'activeAudioNote'
export const USER_STATE = 'user'

export interface DownloadState {
  downloaded?: boolean
  fileName?: string
  percent?: number
  status?: string
}

const GlobalStateUtils: Record<string, any> = {}

GlobalStateUtils.highlightContext = {
  get: (): HighlightContext => {
    // @ts-ignore @todo fix typing here to ensure IPC object safety
    return GlobalState.get(HIGHLIGHT_CONTEXT_STATE)
  },
  set: (context: HighlightContext) => {
    GlobalState.set(HIGHLIGHT_CONTEXT_STATE, context)
  },
  clear: () => {
    GlobalState.delete(HIGHLIGHT_CONTEXT_STATE)
  },
}

GlobalStateUtils.selectableContext = {
  get: (): SelectableContext => {
    // @ts-ignore @todo fix typing here to ensure IPC object safety
    return GlobalState.get(SELECTABLE_CONTEXT_STATE)
  },
  set: (context: SelectableContext) => {
    GlobalState.set(SELECTABLE_CONTEXT_STATE, context)
  },
  clear: () => {
    GlobalState.delete(SELECTABLE_CONTEXT_STATE)
  },
  update: <K extends keyof SelectableContext>(key: K, value: SelectableContext[K]) => {
    // @ts-ignore
    const currentContext = GlobalStateUtils.selectableContext.get()
    const updatedContext = { ...currentContext, [key]: value }
    GlobalState.set(SELECTABLE_CONTEXT_STATE, updatedContext)
  },
  batchUpdate: (context: Partial<SelectableContext>) => {
    // @ts-ignore
    const currentContext = GlobalStateUtils.selectableContext.get()
    const updatedContext = { ...currentContext, ...context }
    GlobalState.set(SELECTABLE_CONTEXT_STATE, updatedContext)
  },
}

GlobalStateUtils.user = {
  get: (): User => {
    // @ts-ignore @todo fix typing here to ensure IPC object safety
    return GlobalState.get(USER_STATE)
  },
  set: (user: User) => {
    GlobalState.set(USER_STATE, user)
  },
  clear: () => {
    GlobalState.delete(USER_STATE)
  },
  update: <K extends keyof User>(key: K, value: User[K]) => {
    // @ts-ignore
    const currentUser = GlobalStateUtils.user.get()
    const updatedUser = { ...currentUser, [key]: value }
    GlobalState.set(USER_STATE, updatedUser)
  },
  /**
   * Listen for changes to the user ID explicitly.
   * Each callback tracks its own previous user to avoid race conditions
   * when multiple callbacks are registered.
   * @param callback
   */
  onUserIdChange: (callback: (user?: User) => void) => {
    // Track previous user per callback to avoid race conditions
    let prevUser: User | undefined = GlobalState.get<User>(USER_STATE)
    const onChange = () => {
      const user = GlobalState.get<User>(USER_STATE)
      // User ID didn't actually change, ignore
      if (user?.id === prevUser?.id) {
        return
      }
      prevUser = user
      callback(user)
    }
    GlobalState.on(USER_STATE, onChange)
    GlobalState.on('update', onChange)
    return () => {
      GlobalState.off(USER_STATE, onChange)
      GlobalState.off('update', onChange)
    }
  },
}

GlobalStateUtils.getDownloadState = (label: string) => GlobalState.get<DownloadState>(label)
GlobalStateUtils.setDownloadState = (label: string, download: DownloadState) => GlobalState.set(label, download)
GlobalStateUtils.clearDownloadState = (label: string) => GlobalState.delete(label)

GlobalStateUtils.getPermissions = (): PermissionsState | undefined => GlobalState.get<PermissionsState>('permissions')

GlobalStateUtils.isSystemSleeping = () => {
  return GlobalState.get('systemSleeping') == true
}

type UpdateStateCallback = (prev?: UpdateState) => UpdateState
GlobalStateUtils.update = {
  get: (): UpdateState | undefined => {
    return GlobalState.get<UpdateState>('update.state')
  },
  set: (state: UpdateState | UpdateStateCallback) => {
    GlobalState.set<UpdateState>('update.state', state)
  },
  clear: () => {
    GlobalState.delete('update.state')
  },
}

export default GlobalStateUtils
